import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Download, ChevronDown, Brain, HardDrive, BarChart2, Layers } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// ─── Model type metadata ────────────────────────────────────────────────────

const MODEL_META = {
  random_forest:       { label: 'Random Forest',   accent: '#10b981', badge: 'bg-emerald-100 text-emerald-800' },
  decision_tree:       { label: 'Decision Tree',   accent: '#22c55e', badge: 'bg-green-100 text-green-800' },
  svc:                 { label: 'SVC',             accent: '#3b82f6', badge: 'bg-blue-100 text-blue-800' },
  logistic_regression: { label: 'Logistic Reg.',  accent: '#6366f1', badge: 'bg-indigo-100 text-indigo-800' },
  gradient_boosting:   { label: 'Gradient Boost', accent: '#8b5cf6', badge: 'bg-violet-100 text-violet-800' },
  linear_regression:   { label: 'Linear Reg.',    accent: '#0ea5e9', badge: 'bg-sky-100 text-sky-800' },
  ridge:               { label: 'Ridge',           accent: '#06b6d4', badge: 'bg-cyan-100 text-cyan-800' },
  lasso:               { label: 'Lasso',           accent: '#14b8a6', badge: 'bg-teal-100 text-teal-800' },
  svr:                 { label: 'SVR',             accent: '#60a5fa', badge: 'bg-blue-100 text-blue-800' },
  elastic_net:         { label: 'Elastic Net',     accent: '#22d3ee', badge: 'bg-cyan-100 text-cyan-800' },
  cnn:                 { label: 'CNN',             accent: '#a855f7', badge: 'bg-purple-100 text-purple-800' },
  mlp:                 { label: 'Neural Net',      accent: '#d946ef', badge: 'bg-fuchsia-100 text-fuchsia-800' },
  lstm:                { label: 'LSTM',            accent: '#ec4899', badge: 'bg-pink-100 text-pink-800' },
  arima:               { label: 'ARIMA',           accent: '#f59e0b', badge: 'bg-amber-100 text-amber-800' },
  sarima:              { label: 'SARIMA',          accent: '#f97316', badge: 'bg-orange-100 text-orange-800' },
  prophet:             { label: 'Prophet',         accent: '#eab308', badge: 'bg-yellow-100 text-yellow-800' },
  kmeans:              { label: 'K-Means',         accent: '#f43f5e', badge: 'bg-rose-100 text-rose-800' },
  dbscan:              { label: 'DBSCAN',          accent: '#ef4444', badge: 'bg-red-100 text-red-800' },
  hierarchical:        { label: 'Hierarchical',    accent: '#dc2626', badge: 'bg-red-100 text-red-800' },
};

const FILTER_GROUPS = {
  All:           null,
  Classification: ['random_forest', 'decision_tree', 'svc', 'logistic_regression', 'gradient_boosting'],
  Regression:    ['linear_regression', 'ridge', 'lasso', 'svr', 'elastic_net'],
  'Deep Learning': ['cnn', 'mlp', 'lstm'],
  'Time Series': ['arima', 'sarima', 'prophet'],
  Clustering:    ['kmeans', 'dbscan', 'hierarchical'],
};

const STATUS_STYLES = {
  completed: 'bg-emerald-100 text-emerald-700',
  training:  'bg-amber-100 text-amber-700',
  failed:    'bg-red-100 text-red-700',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function getMeta(modelType) {
  return MODEL_META[modelType] || { label: modelType, accent: '#6b7280', badge: 'bg-gray-100 text-gray-700' };
}

function isClassification(metrics) {
  return metrics && 'classification_report' in metrics;
}

function isRegression(metrics) {
  return metrics && ('mse' in metrics || 'r2' in metrics || 'mae' in metrics);
}

function getPrimaryMetric(model) {
  const { metrics, model_type } = model;
  if (!metrics) return null;

  if (isClassification(metrics) && typeof metrics.accuracy === 'number') {
    return { label: 'Accuracy', value: metrics.accuracy, asBar: true };
  }
  if (isRegression(metrics) && typeof metrics.r2 === 'number') {
    return { label: 'R²', value: metrics.r2, asBar: true };
  }
  // Time series / clustering — just display a text value if any metric exists
  const firstNumeric = Object.entries(metrics).find(
    ([, v]) => typeof v === 'number'
  );
  if (firstNumeric) {
    return { label: firstNumeric[0].toUpperCase(), value: firstNumeric[1], asBar: false };
  }
  return null;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ─── Skeleton card ───────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
    <div className="h-1 bg-gray-200 w-full" />
    <div className="p-5 space-y-3">
      <div className="flex justify-between items-start">
        <div className="h-4 bg-gray-200 rounded w-2/5" />
        <div className="h-5 bg-gray-200 rounded-full w-14" />
      </div>
      <div className="flex gap-2">
        <div className="h-5 bg-gray-100 rounded-full w-24" />
        <div className="h-5 bg-gray-100 rounded-full w-16" />
      </div>
      <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
        <div className="flex gap-3">
          <div className="h-3 bg-gray-100 rounded w-14" />
          <div className="h-3 bg-gray-100 rounded w-20" />
        </div>
        <div className="flex gap-1">
          <div className="h-6 w-6 bg-gray-100 rounded-lg" />
          <div className="h-6 w-6 bg-gray-100 rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

// ─── Metric bar ──────────────────────────────────────────────────────────────

const MetricBar = ({ label, value, accent }) => {
  const pct = Math.min(Math.max((value || 0) * 100, 0), 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-gray-500">
        <span>{label}</span>
        <span className="font-medium text-gray-700">{(value || 0).toFixed(3)}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: accent }}
        />
      </div>
    </div>
  );
};

// ─── Expanded: Classification ────────────────────────────────────────────────

const ClassificationExpanded = ({ metrics, accent }) => {
  const { classification_report, accuracy } = metrics;
  const macroF1   = classification_report?.['macro avg']?.['f1-score'];
  const weightedF1 = classification_report?.['weighted avg']?.['f1-score'];

  const classEntries = Object.entries(classification_report || {}).filter(
    ([key]) =>
      !['macro avg', 'weighted avg', 'accuracy', 'model_id', 'model', '[[Prototype]]'].includes(key)
  );

  return (
    <div className="border-t border-gray-100 pt-5 mt-5 space-y-6">
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Summary metrics</p>
        <div className="space-y-2.5">
          {typeof accuracy === 'number' && (
            <MetricBar label="Accuracy" value={accuracy} accent={accent} />
          )}
          {typeof macroF1 === 'number' && (
            <MetricBar label="Macro Avg F1-Score" value={macroF1} accent={accent} />
          )}
          {typeof weightedF1 === 'number' && (
            <MetricBar label="Weighted Avg F1-Score" value={weightedF1} accent={accent} />
          )}
        </div>
      </div>

      {classEntries.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Per-class breakdown</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                  <th className="pb-2 font-medium pr-4">Class</th>
                  <th className="pb-2 font-medium pr-4">Precision</th>
                  <th className="pb-2 font-medium pr-4">Recall</th>
                  <th className="pb-2 font-medium pr-4">F1-Score</th>
                  <th className="pb-2 font-medium">Support</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {classEntries.map(([className, cls]) => (
                  <tr key={className} className="text-gray-700">
                    <td className="py-2 pr-4 font-medium text-gray-800">{className}</td>
                    <td className="py-2 pr-4">{cls.precision?.toFixed(3) ?? '—'}</td>
                    <td className="py-2 pr-4">{cls.recall?.toFixed(3) ?? '—'}</td>
                    <td className="py-2 pr-4">{cls['f1-score']?.toFixed(3) ?? '—'}</td>
                    <td className="py-2">{cls.support ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Expanded: Regression ────────────────────────────────────────────────────

const RegressionExpanded = ({ metrics }) => {
  const cards = [
    { label: 'Mean Squared Error (MSE)', value: metrics.mse },
    { label: 'R² Score',                 value: metrics.r2 },
    { label: 'Mean Absolute Error (MAE)', value: metrics.mae },
  ];

  return (
    <div className="border-t border-gray-100 pt-5 mt-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
        Regression metrics
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map(({ label, value }) => (
          <div key={label} className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className="text-lg font-semibold text-gray-800">
              {typeof value === 'number' ? value.toFixed(4) : '—'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Expanded: Other (time series / clustering) ──────────────────────────────

const OtherExpanded = ({ metrics }) => {
  const entries = Object.entries(metrics).filter(([, v]) => typeof v === 'number');

  return (
    <div className="border-t border-gray-100 pt-5 mt-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Metrics</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {entries.map(([key, val]) => (
          <div key={key} className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">{key.toUpperCase()}</p>
            <p className="text-lg font-semibold text-gray-800">{val.toFixed(4)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Model card ──────────────────────────────────────────────────────────────

const ModelCard = ({ model, isExpanded, onToggle, onDownload }) => {
  const meta      = getMeta(model.model_type);
  const primary   = getPrimaryMetric(model);
  const statusCls = STATUS_STYLES[model.status] || 'bg-gray-100 text-gray-600';

  const metricDisplay = primary
    ? primary.asBar
      ? `${(primary.value * 100).toFixed(1)}%`
      : primary.value.toFixed(3)
    : null;

  const renderExpanded = () => {
    if (!model.metrics) return null;
    if (isClassification(model.metrics)) {
      return <ClassificationExpanded metrics={model.metrics} accent={meta.accent} />;
    }
    if (isRegression(model.metrics)) {
      return <RegressionExpanded metrics={model.metrics} />;
    }
    return <OtherExpanded metrics={model.metrics} />;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col overflow-hidden">
      {/* top accent bar */}
      <div className="h-1 w-full" style={{ backgroundColor: meta.accent }} />

      <div className="p-5 flex flex-col flex-1">
        {/* name + metric pill */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-gray-900 leading-snug min-w-0 truncate">
            {model.name || 'Unnamed Model'}
          </h2>
          {metricDisplay && (
            <span
              className="flex-shrink-0 text-xs font-bold px-2.5 py-0.5 rounded-full text-white"
              style={{ backgroundColor: meta.accent }}
              title={primary.label}
            >
              {metricDisplay}
            </span>
          )}
        </div>

        {/* badges */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${meta.badge}`}>
            {meta.label}
          </span>
          {model.dataset_type && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {model.dataset_type}
            </span>
          )}
          {model.status && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${statusCls}`}>
              {model.status}
            </span>
          )}
        </div>

        <div className="flex-1" />

        {/* footer: size + date + actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-gray-50">
          <div className="flex items-center gap-3 text-xs text-gray-400 flex-1 min-w-0">
            {model.model_size_mb != null && (
              <span className="flex items-center gap-1">
                <HardDrive className="w-3 h-3" />
                {model.model_size_mb.toFixed(2)} MB
              </span>
            )}
            {model.created_at && (
              <span className="truncate">{formatDate(model.created_at)}</span>
            )}
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              onClick={() => onDownload(model._id)}
              title="Download model"
              className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onToggle(model._id)}
              title="Toggle metrics"
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
            >
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* expanded metrics */}
        {isExpanded && renderExpanded()}
      </div>
    </div>
  );
};

// ─── Empty state ─────────────────────────────────────────────────────────────

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-24 text-center">
    <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
      <Brain className="w-8 h-8 text-blue-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-800 mb-1">No models yet</h3>
    <p className="text-sm text-gray-400 max-w-xs">
      Train a model in the Pipeline Editor to see it here.
    </p>
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const Models = () => {
  const [models, setModels]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expandedModel, setExpandedModel] = useState(null);
  const [activeFilter, setActiveFilter]   = useState('All');

  const { user } = useAuth();

  useEffect(() => {
    fetchModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchModels = async () => {
    try {
      if (user && user._id) {
        const response = await api.get(`/models/user/${user._id}`);
        setModels(response.data);
      }
    } catch (error) {
      // silently fail — empty state will show
    } finally {
      setLoading(false);
    }
  };

  const downloadModel = async (modelId) => {
    try {
      const response = await api.get(`/models/${modelId}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `model-${modelId}.joblib`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // error is surfaced to user via browser download failure
    }
  };

  const toggleExpanded = (modelId) => {
    setExpandedModel((prev) => (prev === modelId ? null : modelId));
  };

  // ─── Derived stats ───────────────────────────────────────────────────────

  const completedModels = models.filter((m) => m.status === 'completed');

  const avgAccuracy = (() => {
    const accuracies = completedModels
      .map((m) => {
        if (!m.metrics) return null;
        if (isClassification(m.metrics)) return m.metrics.accuracy;
        if (isRegression(m.metrics))     return m.metrics.r2;
        return null;
      })
      .filter((v) => typeof v === 'number');
    if (!accuracies.length) return null;
    return accuracies.reduce((a, b) => a + b, 0) / accuracies.length;
  })();

  const totalStorageMb = models.reduce(
    (acc, m) => acc + (m.model_size_mb || 0),
    0
  );

  // ─── Filtered models ─────────────────────────────────────────────────────

  const allowedTypes = FILTER_GROUPS[activeFilter];
  const filtered = allowedTypes
    ? models.filter((m) => allowedTypes.includes(m.model_type))
    : models;

  // ─── Tab counts ──────────────────────────────────────────────────────────

  const tabCount = (tab) => {
    const types = FILTER_GROUPS[tab];
    if (!types) return models.length;
    return models.filter((m) => types.includes(m.model_type)).length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page header */}
      <div className="bg-gradient-to-b from-white to-blue-50 border-b border-gray-100 px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Brain className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Models</h1>
          </div>
          <p className="text-sm text-gray-500 mb-6 ml-10">
            {models.length === 0
              ? 'No models trained yet'
              : `${models.length} model${models.length !== 1 ? 's' : ''} in your library`}
          </p>

          {/* Stat pills */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm shadow-sm">
              <Layers className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-500">Total</span>
              <span className="font-semibold text-gray-900">{models.length}</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm shadow-sm">
              <BarChart2 className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-gray-500">Avg accuracy / R²</span>
              <span className="font-semibold text-blue-600">
                {avgAccuracy != null ? `${(avgAccuracy * 100).toFixed(1)}%` : '—'}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm shadow-sm">
              <HardDrive className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-500">Storage</span>
              <span className="font-semibold text-gray-900">
                {totalStorageMb >= 1024
                  ? `${(totalStorageMb / 1024).toFixed(2)} GB`
                  : `${totalStorageMb.toFixed(2)} MB`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="bg-white border-b border-gray-100 px-6">
        <div className="max-w-5xl mx-auto flex gap-1 overflow-x-auto">
          {Object.keys(FILTER_GROUPS).map((tab) => {
            const count   = tabCount(tab);
            const active  = tab === activeFilter;
            return (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors duration-150 ${
                  active
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                {tab}
                <span
                  className={`text-xs rounded-full px-1.5 py-0.5 font-medium ${
                    active ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
            {filtered.map((model) => (
              <ModelCard
                key={model._id}
                model={model}
                isExpanded={expandedModel === model._id}
                onToggle={toggleExpanded}
                onDownload={downloadModel}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Models;
